import moment, { Moment } from 'moment';

import { type EventFormData } from '../data-utils';

export const formatFactorForDb = (factor: number) => Number((factor / 100 + 1).toFixed(2));

export const formatFactorToPercents = (factor: number) => Number(((factor - 1) * 100).toFixed());

/**
 * Applies specific date to form intervals which may hold a correct time but incorrect date.
 *
 * @param hoursInterval - TZ hours interval
 * @param calendarEventInterval - interval with correct event start / end date
 * @param timezone - target timezone
 *
 * @return [start, end] - event date-time interval with an hour interval applied on top of the date range
 */
export const getFormIntervalsWithEventDate = ({
	hoursInterval,
	calendarEventInterval,
	timezone
}: {
	hoursInterval: [number, number];
	calendarEventInterval: [Moment, Moment];
	timezone: string;
}): [Moment, Moment] => {
	const tzEventInterval = [
		calendarEventInterval[0].clone().tz(timezone),
		calendarEventInterval[1].clone().tz(timezone)
	];

	return [
		tzEventInterval[0].startOf('hour').hours(hoursInterval[0]),
		tzEventInterval[1].startOf('hour').hours(hoursInterval[1])
	];
};

// Consider processing DST somehow here too, or else getCalendarIntervals / getIntervals might
// provide desynchronized intervals which may affect events splitting
export const getCalendarIntervals = (
	formData: EventFormData,
	timezone: string,
	allDayDate?: Moment
): [Moment, Moment] => {
	if (formData.timeFrame === 'allDay') {
		return allDayDate
			? [allDayDate.clone().tz(timezone).startOf('day'), allDayDate.clone().tz(timezone).endOf('day')]
			: [moment.tz(timezone).startOf('day'), moment.tz(timezone).endOf('day')];
	}
	return formData.intervals;
};

export const getIntervals = (formData: EventFormData): [number, number][] => {
	const { timeFrame } = formData;

	// We populate intervals to the formData on the Create / Edit form exit if 'allDay'
	if (timeFrame == 'specificHours' || timeFrame == 'allDay') {
		// This function used in different validations where TZ isn't provided thus taken UTC as a default value
		const startHour = moment(formData.intervals[0]).utc().hour();

		const endMinute = moment(formData.intervals[1]).utc().minute();
		let endHour = moment(formData.intervals[1]).utc().hour();

		// When intervals set via form in EventDialog values will be: 00:00 and 23:00
		// When intervals set via DnD in TrafficCalendar values will be: 00:00 and 23:59
		// So the latter should be converted to the number: 24 instead of 23
		if (endMinute === 59) endHour++;

		// When editing traffic variable with endHour 24:00 on calendar
		// not changing time window results in endHour === 0 on the data layer
		// which in turn result in wrong time range like [20, 0] instead of [20, 24]
		if (endHour === 0) endHour = 24;

		return [[startHour, endHour]];
	} else {
		throw new Error('There is incorrect timeFrame on in form!');
	}
};
